import Modal from "@/components/atoms/Modal";
import { INVALID_FORM_MESSAGE } from "@/constants/validatorMessages";
import { IModalMethods } from "@/interfaces/Modal";
import { OgranisationForm } from "@/interfaces/Organisation";
import { useStoreOrganisationMutation } from "@/services/organisations";

import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import { SubmitErrorHandler, SubmitHandler, useForm } from "react-hook-form";
import { ToastContentProps, toast } from "react-toastify";

const StoreOrganisationModal: ForwardRefRenderFunction<IModalMethods> = (
  _,
  ref
) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const [storeOrganisationAsync] = useStoreOrganisationMutation();
  const {
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<OgranisationForm>({
    defaultValues: {
      backgroundColor: "#1e1a55",
      textColor: "#ffffff",
    },
  });

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<OgranisationForm> = (data) => {
    showOrHiddenModal();
    toast.promise(storeOrganisationAsync(data).unwrap(), {
      pending: "Ajout de l'organisation en cours...",
      success: {
        render() {
          return (
            <p style={{ marginBottom: 0, textAlign: "center" }}>
              L'organisation à bien été ajouté 🤩
            </p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          showOrHiddenModal();
          if (data?.status === 422) {
            return "Les champs que vous avez remplis semblent être incorrects.";
          }

          return "Une erreur est survenue";
        },
      },
    });
  };

  const onError: SubmitErrorHandler<OgranisationForm> = () =>
    toast.error(INVALID_FORM_MESSAGE);

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter une organisation"
      onSubmit={handleSubmit(onSubmit, onError)}
    >
      <Row className="gx-3">
        <Col sm={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Nom <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="ex : Google"
              isInvalid={!!errors.name}
              {...register("name", {
                required: "Le nom est requis.",
                minLength: {
                  value: 3,
                  message: "Ce champ doit contenir au moins 3 caractères.",
                },
                maxLength: {
                  value: 255,
                  message: "Ce champ doit contenir au plus 255 caractères.",
                },
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.name?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Séléctionne la couleur du texte{" "}
              <span className="text-danger">*</span>{" "}
            </Form.Label>{" "}
            :
            <Form.Control
              type="color"
              className="w-100"
              {...register("textColor", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.textColor}
            />
            <Form.Control.Feedback type="invalid">
              {errors.textColor?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Séléctionne la couleur de fond{" "}
              <span className="text-danger">*</span>
            </Form.Label>
            :
            <Form.Control
              type="color"
              className="w-100"
              {...register("backgroundColor", {
                required: "Ce champ est obligatoire",
              })}
              isInvalid={!!errors.backgroundColor}
            />
            <Form.Control.Feedback type="invalid">
              {errors.backgroundColor?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Lien du catalogue :</Form.Label>
            <Form.Control
              type="url"
              placeholder="https://exemple.com/catalogue"
              isInvalid={!!errors.catalogUrl}
              {...register("catalogUrl", {
                pattern: {
                  value: /^https?:\/\/.+/,
                  message: "L'URL doit commencer par http:// ou https://",
                },
              })}
            />
            <Form.Control.Feedback type="invalid">
              {errors.catalogUrl?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>
              Convention PDF <span className="text-danger">*</span> :
            </Form.Label>
            <Form.Control
              type="file"
              accept=".pdf"
              {...register("convention", {
                required: {
                  value: true,
                  message: "La convention est obligatoire",
                },
              })}
              isInvalid={!!errors.convention}
            />
            <Form.Control.Feedback type="invalid">
              {errors.convention?.message}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreOrganisationModal);
